import { Code, ConnectError } from '@connectrpc/connect'
import {
  SplitSupplyNoticeErrorReason,
  SplitSupplyNoticeErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  readAppErrorInfo,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'
import { mapErrorToMessage } from '~/libs/mapConnectError'

const KEY_PREFIX = 'page:splitSupplyPlan.serverError.reason' as const

/**
 * `SplitSupplyNoticeErrorReason` → i18n key の registry。 `Record<enum, entry | undefined>` で
 * 網羅を型で強制する (docs/adr/structured-app-error-standard-errorinfo-envelope.md)。
 *
 * 一覧 (計画作成) と詳細 (保存・ダウンロード) は欄を持たない操作なので、 すべて
 * operation-scoped (`field` を持たない) で、 トースト・上部の案内に出す。
 */
export const SPLIT_SUPPLY_NOTICE_REASON_KEYS: Record<
  SplitSupplyNoticeErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [SplitSupplyNoticeErrorReason.UNSPECIFIED]: undefined,
  [SplitSupplyNoticeErrorReason.NOTICE_DATE_CLOSED]: {
    key: `${KEY_PREFIX}.noticeDateClosed`,
  },
  [SplitSupplyNoticeErrorReason.NOTICE_POINTS_CHANGED]: {
    key: `${KEY_PREFIX}.noticePointsChanged`,
  },
  [SplitSupplyNoticeErrorReason.NOTICE_NOT_FOUND]: {
    key: `${KEY_PREFIX}.noticeNotFound`,
  },
  [SplitSupplyNoticeErrorReason.NOTICE_VALUE_OUT_OF_RANGE]: {
    key: `${KEY_PREFIX}.noticeValueOutOfRange`,
  },
  [SplitSupplyNoticeErrorReason.NOTICE_DELIVERY_DATE_OUT_OF_PERIOD]: {
    key: `${KEY_PREFIX}.noticeDeliveryDateOutOfPeriod`,
  },
  [SplitSupplyNoticeErrorReason.XML_SPLIT_REQUIRED]: {
    key: `${KEY_PREFIX}.xmlSplitRequired`,
  },
  [SplitSupplyNoticeErrorReason.NOTICE_TOO_MANY_POINTS]: {
    key: `${KEY_PREFIX}.noticeTooManyPoints`,
  },
  [SplitSupplyNoticeErrorReason.PARTNER_NOTICE_NOT_FOUND]: {
    key: `${KEY_PREFIX}.partnerNoticeNotFound`,
  },
  [SplitSupplyNoticeErrorReason.PARTNER_NOTICE_NO_POINTS]: {
    key: `${KEY_PREFIX}.partnerNoticeNoPoints`,
  },
  [SplitSupplyNoticeErrorReason.PARTNER_NOTICE_POINTS_CHANGED]: {
    key: `${KEY_PREFIX}.partnerNoticePointsChanged`,
  },
  [SplitSupplyNoticeErrorReason.NOTICE_DAYS_INVALID]: {
    key: `${KEY_PREFIX}.noticeDaysInvalid`,
  },
  [SplitSupplyNoticeErrorReason.PARTNER_NOTICE_IMPORT_UPLOAD_EXPIRED]: {
    key: `${KEY_PREFIX}.partnerNoticeImportUploadExpired`,
  },
  [SplitSupplyNoticeErrorReason.PARTNER_NOTICE_IMPORT_INVALID]: {
    key: `${KEY_PREFIX}.partnerNoticeImportInvalid`,
  },
  [SplitSupplyNoticeErrorReason.PARTNER_NOTICE_IMPORT_NOTHING]: {
    key: `${KEY_PREFIX}.partnerNoticeImportNothing`,
  },
  [SplitSupplyNoticeErrorReason.PARTNER_NOTICE_IMPORT_CHANGED]: {
    key: `${KEY_PREFIX}.partnerNoticeImportChanged`,
  },
  [SplitSupplyNoticeErrorReason.PARTNER_NOTICE_IMPORT_TOO_LARGE]: {
    key: `${KEY_PREFIX}.partnerNoticeImportTooLarge`,
  },
  [SplitSupplyNoticeErrorReason.OWN_NOTICE_IMPORT_UPLOAD_EXPIRED]: {
    key: `${KEY_PREFIX}.ownNoticeImportUploadExpired`,
  },
  [SplitSupplyNoticeErrorReason.OWN_NOTICE_IMPORT_INVALID]: {
    key: `${KEY_PREFIX}.ownNoticeImportInvalid`,
  },
  [SplitSupplyNoticeErrorReason.OWN_NOTICE_IMPORT_NOTHING]: {
    key: `${KEY_PREFIX}.ownNoticeImportNothing`,
  },
  [SplitSupplyNoticeErrorReason.OWN_NOTICE_IMPORT_CHANGED]: {
    key: `${KEY_PREFIX}.ownNoticeImportChanged`,
  },
  [SplitSupplyNoticeErrorReason.OWN_NOTICE_IMPORT_TOO_LARGE]: {
    key: `${KEY_PREFIX}.ownNoticeImportTooLarge`,
  },
  [SplitSupplyNoticeErrorReason.OWN_NOTICE_TEMPLATE_NO_POINTS]: {
    key: `${KEY_PREFIX}.ownNoticeTemplateNoPoints`,
  },
}

/**
 * 自社の通告値の操作が失敗した時の表示文言。 reason があればその文言、 無ければ gRPC code の
 * 汎用文言 (楽観ロックの競合 = Aborted は「他の人が先に保存しました」 の案内に差し替える)。
 */
export function describeSplitSupplyNoticeError(
  err: unknown,
  t: TFunction,
): string {
  if (err instanceof ConnectError) {
    const resolved = resolveAppErrorInfoMessage(
      err,
      SplitSupplyNoticeErrorReasonSchema,
      SPLIT_SUPPLY_NOTICE_REASON_KEYS,
      t,
    )
    if (resolved !== undefined) return resolved.message
  }
  return mapErrorToMessage(err, t, {
    // Aborted = 他の人が先に保存した (楽観ロック)。 汎用文言ではなく再読込の案内にする
    [Code.Aborted]: 'page:splitSupplyPlan.serverError.conflict',
  })
}

/**
 * 他社の通告値の削除が失敗した時の表示文言。 消せない期間の登録 (値を保存したことのある過ぎた登録) を
 * 拒んだ時 (NOTICE_DATE_CLOSED) は、 作成・編集の文言ではなく削除の文言にする。 それ以外は
 * `describeSplitSupplyNoticeError` と同じ。
 */
export function describeSplitSupplyNoticeDeleteError(
  err: unknown,
  t: TFunction,
): string {
  if (
    err instanceof ConnectError &&
    readAppErrorInfo(err, SplitSupplyNoticeErrorReasonSchema)?.reason ===
      SplitSupplyNoticeErrorReason.NOTICE_DATE_CLOSED
  ) {
    return t('page:splitSupplyPlan.serverError.partnerDeleteDateClosed')
  }
  return describeSplitSupplyNoticeError(err, t)
}
